import React, { memo, useCallback, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import GlassCard from '../card/GlassCard';
import { themeColors } from 'helpers/utils';
import { getBarTrendline } from 'helpers/chartjs-utils';
import { bitCols, defaultChartOptions } from 'services/glass-management/glassManagement';
import dayjs from 'dayjs';
import SLAChart from '../SLAChart';
import { useSelector, useDispatch } from 'react-redux';
import { openModal } from 'slices/glassDashboard/glassDataModalSlice';

const OtherIssues = () => {
    const [options, setOptions] = useState(defaultChartOptions);
    const dispatch = useDispatch();
    const { data, loading, error } = useSelector(s => s.glassOtherIssues);
    if (error)
        toast.error('Something went wrong!', error);

    const cols = bitCols.filter(x => x.accessor != 'odrJobNo');

    const formatData = useCallback(tableData =>
        tableData?.map(x => {
            return {
                ...x,
                created: dayjs(x.created).format('DD-MM-YYYY'),
            }
        }), []);

    const onChartClick = useCallback((e) => {
        if (data?.length && e?.name) {
            dispatch(openModal({
                modalData: formatData(data?.find(x => x.key === e?.name)?.jobs),
                modalLoading: false,
                modalShow: true,
                modalTitle: 'Other Issues',
                tableCols: cols,
            }));
        }
    }, [dispatch, data]);

    const showAllData = useCallback(() =>
        dispatch(openModal({
            modalData: formatData(Object.keys(data).flatMap(x => data[x].jobs)),
            modalLoading: false,
            modalShow: true,
            modalTitle: 'All Other Issues',
            tableCols: cols,
        })), [dispatch, data]);

    const getOptions = useCallback(() => {
        const xAxis = data?.map(x => x.key);
        const photos = data?.map(x => x.jobs?.filter(y => y.photos?.length > 0))
            .map((x, idx) => {
                return {
                    value: x?.length,
                    name: xAxis[idx],
                };
            });
        const noPhotos = data?.map(x => x.jobs.filter(y => y.photos?.length == 0))
            .map((x, idx) => {
                return {
                    value: x?.length,
                    name: xAxis[idx],
                };
            });
        const chartData = xAxis?.map((name, idx) => {
            return {
                value: data[idx]?.jobs?.length,
                name,
                itemStyle: {
                    color: themeColors.secondary
                }
            };
        });

        return {
            ...defaultChartOptions,
            xAxis: {
                ...defaultChartOptions.xAxis,
                data: xAxis?.map(x => dayjs(x).format('ddd DD'))
            },
            tooltip: {
                trigger: 'axis',
                show: true,
                formatter: params => `${params[0]?.axisValue}: <b>${params[0]?.value + params[1]?.value}</b>`
            },
            legend: {
                data: ['Photos', 'No Photos']
            },
            series: [
                {
                    type: 'bar',
                    stack: 'Total',
                    name: 'No Photos',
                    data: noPhotos,
                    itemStyle: {
                        color: themeColors.danger
                    }
                },
                {
                    type: 'bar',
                    stack: 'Total',
                    name: 'Photos',
                    data: photos,
                    itemStyle: {
                        color: themeColors.secondary
                    }
                },
                {
                    ...defaultChartOptions.series[1],
                    data: getBarTrendline(chartData?.map(x => x.value))
                }
            ],
        }
    }, [data]);

    useEffect(() => setOptions(getOptions()), [data]);

    return (
        <>
            <GlassCard
                md={6}
                lg={4}
                title="Other Issues"
                heroValue={{ value: data?.reduce((a, b) => b?.jobs?.length + a, 0), prefix: '', class: 'text-secondary', func: showAllData }}>
                <SLAChart loading={loading} options={options} onClick={onChartClick} />
            </GlassCard>
        </>
    );
}

export default memo(OtherIssues);